import axios from 'axios';
import { defineStore, storeToRefs } from 'pinia';
import { computed, Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useToast } from '@rygen/rygen-ui/composables';
import { Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import {
  CreditUsage,
  Order,
  Shipment,
  TenderAuthorizationDecision,
} from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { useMessageStore } from '@/stores/Message';
import { useParentEntityStore } from '@/stores/ParentEntity';

const MESSAGE_KEY = 'credit-limit-message';

export interface PerformLoadOptions {
  bypassCache?: boolean;
}

export interface CreditUsageStore {
  creditUsage: Ref<Loadable<CreditUsage>>;
  canTenderToCarrier: Ref<boolean>;
  wouldExceedCreditLimit: Ref<boolean>;
  tenderAuthorization: Ref<Loadable<TenderAuthorizationDecision>>;
  performTenderAuthorization: (
    rateQuoteId: number,
    opts?: PerformLoadOptions,
  ) => Promise<void>;
  loadCreditUsage: (opts?: PerformLoadOptions) => Promise<void>;
}

const _useCreditUsageStore = defineStore(
  'creditUsage',
  (): CreditUsageStore => {
    const { currentUser, is3PLClientOrServiceProvider, isAuthenticatedUser } =
      useCurrentUserStore();
    const { t } = useI18n();
    const { addMessage, removeMessage } = useMessageStore();
    const { parentEntity } = useParentEntityStore();
    const toast = useToast();

    const creditUsage: Ref<Loadable<CreditUsage>> = ref({
      loading: false,
    });
    const tenderAuthorization: Ref<Loadable<TenderAuthorizationDecision>> = ref(
      { loading: false },
    );

    const canOverrideCreditLimit = computed(() =>
      authorization.hasAuthority('client', 'overrideCreditLimit'),
    );

    const canTenderToCarrier = computed(() => {
      return canOverrideCreditLimit.value || !wouldExceedCreditLimit.value;
    });

    const wouldExceedCreditLimit = computed(() => {
      return tenderAuthorization.value.data?.approved === false;
    });

    const loadCreditUsage = (_opts: PerformLoadOptions = {}) => {
      return performCreditUsageLoad();
    };

    const performTenderAuthorization = async (rateQuoteId: number) => {
      if (!tenderAuthorization.value.loading) {
        await performTenderAuthorizationLoad(rateQuoteId);
      }
    };

    const setCreditLimitMessage = async () => {
      try {
        const status = creditUsage.value.data;
        if (status?.standing === 'APPROACHING_LIMIT') {
          addMessage({
            id: MESSAGE_KEY,
            text: t('creditLimits.approachingLimitMessage'),
            severity: 'warn',
            icon: 'pi pi-exclamation-triangle',
          });
        } else if (status?.standing === 'LIMIT_EXCEEDED') {
          addMessage({
            id: MESSAGE_KEY,
            text: t('creditLimits.exceededLimitMessage'),
            severity: 'error',
            icon: 'pi pi-exclamation-triangle',
          });
        } else {
          removeMessage(MESSAGE_KEY);
        }
      } catch (e) {
        if (axios.isCancel(e)) {
          return;
        }

        console.error('Error loading credit limit status', e);
        toast.addError({
          error: e,
          detail: t('errors.loadingParam', {
            param: t('creditLimits.creditLimits'),
          }),
        });
        removeMessage(MESSAGE_KEY);
      }
    };

    const performCreditUsageLoad = async () => {
      if (!isAuthenticatedUser.value || !is3PLClientOrServiceProvider.value) {
        return;
      }

      try {
        creditUsage.value.loading = true;
        creditUsage.value.data = await api.client.getCreditUsage();
        await setCreditLimitMessage();
      } catch (e) {
        if (axios.isCancel(e)) {
          return;
        }

        creditUsage.value.error = e;
        console.error('Error loading credit limit status', e);
        toast.addError({
          detail: t('errors.loadingParam', {
            param: t('creditLimits.creditLimits'),
          }),
          error: e,
        });
      } finally {
        creditUsage.value.loading = false;
      }
    };

    const performTenderAuthorizationLoad = async (rateQuoteId: number) => {
      tenderAuthorization.value.error = undefined;

      if (
        !is3PLClientOrServiceProvider.value ||
        !(
          parentEntity.value instanceof Order ||
          parentEntity.value instanceof Shipment
        )
      ) {
        tenderAuthorization.value.data = { approved: true };
        return;
      }

      if (creditUsage.value.data?.limitConfigured === false) {
        tenderAuthorization.value.data = { approved: true };
        return;
      }

      try {
        tenderAuthorization.value.loading = true;
        if (parentEntity.value.id === undefined) {
          throw new Error(
            'Parent entity id and rate quote id required to authorize tender',
          );
        }
        tenderAuthorization.value.data =
          parentEntity.value instanceof Order
            ? await api.order.authorizeTender(
                parentEntity.value.id,
                rateQuoteId,
              )
            : await api.shipment.authorizeTender(
                parentEntity.value.id,
                rateQuoteId,
              );
      } catch (e) {
        if (axios.isCancel(e)) {
          return;
        }

        console.error('Error loading tenderAuthorization', e);
        tenderAuthorization.value.error = t('errors.loadingParam', {
          param: t('creditLimits.creditLimits'),
        });
        tenderAuthorization.value.data = { approved: false };
      } finally {
        tenderAuthorization.value.loading = false;
      }
    };

    const reset = () => {
      creditUsage.value = { loading: false };
      tenderAuthorization.value = { loading: false };
      removeMessage(MESSAGE_KEY);
    };

    watch(currentUser, () => {
      reset();
      loadCreditUsage();
    });

    watch(parentEntity, () => {
      reset();
      loadCreditUsage();
    });

    return {
      creditUsage,
      canTenderToCarrier,
      wouldExceedCreditLimit,
      tenderAuthorization,
      performTenderAuthorization,
      loadCreditUsage,
    };
  },
);

export const useCreditUsageStore = (): CreditUsageStore => {
  const store = _useCreditUsageStore();
  const {
    creditUsage,
    canTenderToCarrier,
    wouldExceedCreditLimit,
    tenderAuthorization,
  } = storeToRefs(store);
  const { loadCreditUsage, performTenderAuthorization } = store;

  return {
    creditUsage,
    canTenderToCarrier,
    wouldExceedCreditLimit,
    tenderAuthorization,
    performTenderAuthorization,
    loadCreditUsage,
  };
};
